import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import websocketService from '@/services/websocket.service'
import { RT_LOCAL } from '@/services/realtime-events'
import NotificationsApi from '@/services/notifications.api'
import { isDurableNotificationReader } from '@/services/notifications.admission'
import { channels } from '@/config/websocket.config'
import { useAuthStore } from '@/stores/auth'

export interface NotificationItem {
  id: string
  title: string
  message: string
  type: 'info' | 'warning' | 'error' | 'success' | 'system'
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  category?: string
  source?: string
  timestamp: string | Date
  read?: boolean
  pinned?: boolean
  archived?: boolean
  data?: any
  origin?: 'backend' | 'realtime' | 'cache' | 'local'
  persisted?: boolean
  pendingReadSync?: boolean
  syncError?: string | null
  localOnlyReason?: string | null
}

type NotificationActionResult = {
  success: boolean
  code?: string
  message?: string
  backendSynced?: boolean
}

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<NotificationItem[]>([])
  const initialized = ref(false)
  const syncing = ref(false)
  const lastSyncAt = ref<string | null>(null)
  const syncError = ref<string | null>(null)
  const usingCachedData = ref(false)
  const realtimeConnected = ref(false)
  const realtimeStatus = ref<'idle' | 'connecting' | 'connected' | 'disconnected' | 'error'>('idle')
  const lastRealtimeAt = ref<string | null>(null)
  const localActionNotice = ref<string | null>(null)
  const backendUnreadCount = ref<number | null>(null)
  /**
   * WS4 · H2 — whether the backend admits THIS identity to the durable reader (`GET /notifications`,
   * `GET /notifications/unread-count`, `PATCH /notifications/:id/read`). `null` until `initialize()` has read
   * the session; `false` puts the store in REALTIME-ONLY mode: no cache restore of previously synced durable
   * rows, no REST sync, no REST mark-read, and no "degraded / cache" status — the capability is not sold to
   * this identity, so nothing may present it as pending, cached or failing.
   */
  const durableReaderAdmitted = ref<boolean | null>(null)

  const unreadCount = computed(() => items.value.filter(n => !n.read).length)
  const last10 = computed(() => items.value.slice(0, 10))
  const isSynced = computed(() => !usingCachedData.value && !syncError.value && !!lastSyncAt.value)
  const isSyncing = computed(() => syncing.value)
  const realtimeOnly = computed(() => durableReaderAdmitted.value === false)

  function normalizeError(error: unknown): string {
    if (error instanceof Error) return error.message
    if (typeof error === 'string') return error
    return 'No se pudo sincronizar notificaciones con el backend'
  }

  function isBackendBackedId(id: string | number): boolean {
    const normalized = String(id)
    return /^\d+$/.test(normalized) && Number(normalized) > 0
  }

  function createLocalId(): string {
    return globalThis.crypto?.randomUUID?.() || `local-${new Date().getTime()}`
  }

  function mapApiNotification(n: any): NotificationItem {
    return {
      id: String(n.id),
      title: n.title || 'Notificación',
      message: n.message || '',
      type: (n.type as any) || 'system',
      priority: (n.data?.priority as any) || 'medium',
      category: n.data?.category,
      source: n.data?.source || 'Sistema',
      timestamp: n.createdAt || new Date().toISOString(),
      read: !!n.read,
      pinned: false,
      archived: false,
      data: n.data,
      origin: 'backend',
      persisted: true,
      pendingReadSync: false,
      syncError: null,
      localOnlyReason: null
    }
  }

  function add(payload: Partial<NotificationItem>): NotificationItem {
    const id = String(payload.id || createLocalId())
    const existing = items.value.find(n => String(n.id) === id)
    if (existing) {
      Object.assign(existing, {
        ...payload,
        id,
        read: payload.read ?? existing.read ?? false,
        origin: payload.origin || existing.origin,
        persisted: payload.persisted ?? existing.persisted ?? isBackendBackedId(id),
        syncError: payload.syncError ?? existing.syncError ?? null
      })
      persist()
      return existing
    }

    const n: NotificationItem = {
      id,
      title: payload.title || 'Notificación',
      message: payload.message || '',
      type: (payload.type as any) || 'info',
      priority: payload.priority || 'medium',
      category: payload.category,
      source: payload.source,
      timestamp: payload.timestamp || new Date().toISOString(),
      read: payload.read ?? false,
      pinned: payload.pinned ?? false,
      archived: payload.archived ?? false,
      data: payload.data,
      origin: payload.origin || (isBackendBackedId(id) ? 'backend' : 'local'),
      persisted: payload.persisted ?? isBackendBackedId(id),
      pendingReadSync: payload.pendingReadSync ?? false,
      syncError: payload.syncError ?? null,
      localOnlyReason: payload.localOnlyReason ?? null
    }
    items.value.unshift(n)
    // Limitar tamaño para no crecer indefinidamente
    if (items.value.length > 200) items.value = items.value.slice(0, 200)
    persist()
    return n
  }

  function markAsRead(id: string) {
    const it = items.value.find(n => n.id === id)
    if (it) {
      it.read = true
      it.localOnlyReason = isBackendBackedId(id) ? null : 'LOCAL_NOTIFICATION_ONLY'
      persist()
    }
  }

  function registerLocalOnlyAction(action: string): NotificationActionResult {
    const message = `${action} no tiene endpoint backend en este runtime.`
    localActionNotice.value = message
    return {
      success: false,
      code: 'LOCAL_ONLY_ACTION',
      message,
      backendSynced: false
    }
  }

  function markAllAsRead(): NotificationActionResult {
    return registerLocalOnlyAction('markAllAsRead')
  }

  function remove(id: string): NotificationActionResult {
    const it = items.value.find(n => n.id === id)
    if (it) it.localOnlyReason = 'DELETE_NOT_BACKEND_BACKED'
    return registerLocalOnlyAction('remove')
  }

  function togglePin(id: string): NotificationActionResult {
    const it = items.value.find(n => n.id === id)
    if (it) it.localOnlyReason = 'PIN_NOT_BACKEND_BACKED'
    return registerLocalOnlyAction('togglePin')
  }

  function archive(id: string): NotificationActionResult {
    const it = items.value.find(n => n.id === id)
    if (it) it.localOnlyReason = 'ARCHIVE_NOT_BACKEND_BACKED'
    return registerLocalOnlyAction('archive')
  }

  function clear(): NotificationActionResult {
    return registerLocalOnlyAction('clear')
  }

  function persist() {
    // realtime-only identities keep nothing between sessions: a durable cache is the durable reader by
    // another name, and the next mount would present it as "cache" of a sync this identity never gets
    if (realtimeOnly.value) return
    try {
      const toSave = items.value.slice(0, 50)
      localStorage.setItem('mgi.notifications', JSON.stringify(toSave))
    } catch (e) {
      syncError.value = normalizeError(e)
    }
  }

  function restore() {
    if (realtimeOnly.value) {
      // drop whatever an earlier (admitted or pre-containment) session left behind
      try { localStorage.removeItem('mgi.notifications') } catch { /* nothing to drop */ }
      return
    }
    try {
      const raw = localStorage.getItem('mgi.notifications')
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed) && parsed.length > 0) {
          items.value = parsed.map((n: any) => ({
            ...n,
            id: String(n.id),
            origin: 'cache',
            persisted: false,
            pendingReadSync: false,
            syncError: null,
            localOnlyReason: 'RESTORED_FROM_LOCAL_CACHE'
          }))
          usingCachedData.value = true
        }
      }
    } catch (error) {
      usingCachedData.value = true
      syncError.value = normalizeError(error)
    }
  }

  // PR-20: Handler ref nombrado + guard contra doble registro
  let _wsHandlerAttached = false
  const _handleNotificationReceived = (data: any) => {
    lastRealtimeAt.value = new Date().toISOString()
    add({
      id: data?.id,
      title: data?.title || 'Notificación',
      message: data?.message || JSON.stringify(data?.payload || data),
      type: data?.type || 'system',
      priority: data?.priority || 'medium',
      category: data?.category,
      source: data?.source || 'Sistema',
      timestamp: data?.timestamp || new Date().toISOString(),
      read: false,
      data,
      origin: 'realtime',
      persisted: isBackendBackedId(data?.id || ''),
      localOnlyReason: isBackendBackedId(data?.id || '') ? null : 'REALTIME_ONLY_OR_NOT_YET_PERSISTED'
    })
  }

  let _socketStatusHandlersAttached = false
  function attachSocketStatusHandlers() {
    if (_socketStatusHandlersAttached) return
    websocketService.on(RT_LOCAL.SOCKET_CONNECT, () => {
      realtimeConnected.value = true
      realtimeStatus.value = 'connected'
      lastRealtimeAt.value = new Date().toISOString()
    })
    websocketService.on(RT_LOCAL.SOCKET_DISCONNECT, () => {
      realtimeConnected.value = false
      realtimeStatus.value = 'disconnected'
    })
    websocketService.on(RT_LOCAL.SOCKET_CONNECT_ERROR, (error: unknown) => {
      realtimeConnected.value = false
      realtimeStatus.value = 'error'
      syncError.value = normalizeError(error)
    })
    websocketService.on(RT_LOCAL.SOCKET_RECONNECT, () => {
      realtimeConnected.value = true
      realtimeStatus.value = 'connected'
      lastRealtimeAt.value = new Date().toISOString()
    })
    websocketService.on(RT_LOCAL.SOCKET_RECONNECT_ERROR, (error: unknown) => {
      realtimeConnected.value = false
      realtimeStatus.value = 'error'
      syncError.value = normalizeError(error)
    })
    _socketStatusHandlersAttached = true
  }

  async function syncFromApi() {
    // WS4 · H2: the backend refuses the durable reader for this identity; a request that can only answer
    // 403 is not a sync attempt, and its refusal must not be presented as degradation
    if (realtimeOnly.value) return
    syncing.value = true
    try {
      const list = await NotificationsApi.list()
      let unreadCountError: string | null = null
      try {
        backendUnreadCount.value = await NotificationsApi.unreadCount()
      } catch (error) {
        backendUnreadCount.value = null
        unreadCountError = normalizeError(error)
      }

      const backendItems = list.map(mapApiNotification)
      const backendIds = new Set(backendItems.map(n => String(n.id)))
      const realtimeOrLocalItems = items.value.filter(n =>
        n.origin !== 'cache' && !backendIds.has(String(n.id)) && !n.archived
      )

      items.value = [...realtimeOrLocalItems, ...backendItems].sort((a, b) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      })
      usingCachedData.value = false
      syncError.value = unreadCountError
      lastSyncAt.value = new Date().toISOString()
      persist()
    } catch (error) {
      usingCachedData.value = true
      syncError.value = normalizeError(error)
    } finally {
      syncing.value = false
    }
  }

  async function initialize() {
    if (initialized.value) return
    const auth = useAuthStore()
    // decide the mode from the session BEFORE anything is restored or requested
    durableReaderAdmitted.value = auth.isAuthenticated ? isDurableNotificationReader(auth.user?.role) : null
    restore()
    attachSocketStatusHandlers()

    // Intentar conectar si no está conectado
    try {
      realtimeStatus.value = 'connecting'
      await websocketService.connect()
      realtimeConnected.value = true
      realtimeStatus.value = 'connected'
    } catch (error) {
      realtimeConnected.value = false
      realtimeStatus.value = 'error'
      syncError.value = normalizeError(error)
    }
    // Suscribirse al canal global de notificaciones
    if (!_wsHandlerAttached) {
      websocketService.on(RT_LOCAL.NOTIFICATION_RECEIVED, _handleNotificationReceived)
      _wsHandlerAttached = true
    }
    // Intentar suscripción al canal de notificaciones
    // También canales de usuario si hay sesión
    if (auth.user) {
      websocketService.subscribeUserChannels(auth.user)
    }
    // Sincronizar con backend (historial y contador)
    // Evitar 401 en arranque si no hay sesión real
    if (!auth.isAuthenticated) {
      initialized.value = true
      return
    }
    await syncFromApi()
    initialized.value = true
  }

  async function markAsReadAndSync(id: string): Promise<NotificationActionResult> {
    const item = items.value.find(n => String(n.id) === String(id))
    if (!item) {
      return {
        success: false,
        code: 'NOTIFICATION_NOT_FOUND',
        message: 'Notificación no encontrada',
        backendSynced: false
      }
    }

    // Intentar marcar en backend si id es numérico — y sólo si el backend admite a esta identidad en el
    // lector durable (WS4 · H2); en modo realtime-only la lectura queda local, como la de un id no numérico
    if (!isBackendBackedId(id) || realtimeOnly.value) {
      item.read = true
      item.localOnlyReason = 'LOCAL_NOTIFICATION_ONLY'
      item.syncError = null
      persist()
      return {
        success: true,
        code: 'LOCAL_ONLY_ACTION',
        message: 'Notificación local marcada como leída solo en este navegador.',
        backendSynced: false
      }
    }

    const wasRead = !!item.read
    item.read = true
    item.pendingReadSync = true
    item.syncError = null
    persist()

    try {
      await NotificationsApi.markAsRead(Number(id))
      item.pendingReadSync = false
      item.syncError = null
      item.persisted = true
      item.origin = 'backend'
      syncError.value = null
      persist()
      return {
        success: true,
        backendSynced: true
      }
    } catch (error) {
      const message = normalizeError(error)
      item.read = wasRead
      item.pendingReadSync = false
      item.syncError = message
      syncError.value = message
      persist()
      return {
        success: false,
        code: 'NOTIFICATION_MARK_READ_FAILED',
        message,
        backendSynced: false
      }
    }
  }

  return {
    // state
    items,
    initialized,
    syncing,
    isInitialized: initialized,
    isSyncing,
    isSynced,
    lastSyncAt,
    syncError,
    usingCachedData,
    realtimeConnected,
    realtimeStatus,
    lastRealtimeAt,
    localActionNotice,
    backendUnreadCount,
    durableReaderAdmitted,
    // getters
    unreadCount,
    last10,
    realtimeOnly,
    // actions
    add,
    markAsRead,
    markAsReadAndSync,
    markAllAsRead,
    remove,
    togglePin,
    archive,
    clear,
    initialize,
    syncFromApi,
    isBackendBackedId
  }
})
